import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, GitCompare, Eye, Armchair, Timer, Target, Droplets, Thermometer, Trophy, Palette, Sparkles, Camera } from "lucide-react";
import { ShopifyProduct, CartItem } from "@/lib/shopify";
import { isForceSoldOut, getEffectiveAvailability } from "@/lib/inventoryOverrides";
import { useInventoryOverride } from "@/hooks/useInventoryOverrides";
import { useCartStore } from "@/stores/cartStore";
import { useWishlist } from "@/contexts/WishlistContext";
import { useCompare } from "@/contexts/CompareContext";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface FeatureBadge {
  icon: "cushioning" | "extended-play" | "ergonomic" | "moisture" | "temperature" | "tournament" | "design" | "premium" | "instagram";
  label: string;
}

interface ShopifyProductCardProps {
  product: ShopifyProduct;
  showTopRatedBadge?: boolean;
  customBadge?: {
    text: string;
    className?: string;
  };
  featureBadges?: FeatureBadge[];
}
export const ShopifyProductCard = ({
  product,
  showTopRatedBadge = false,
  customBadge,
  featureBadges
}: ShopifyProductCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showQuickShop, setShowQuickShop] = useState(false);
  const addItem = useCartStore(state => state.addItem);
  const {
    isInWishlist,
    toggleItem
  } = useWishlist();
  const {
    isInCompare,
    addToCompare,
    removeFromCompare,
    compareItems
  } = useCompare();
  const {
    node
  } = product;
  const imageUrl = node.images?.edges?.[0]?.node?.url;
  const secondImageUrl = node.images?.edges?.[1]?.node?.url;
  
  // Check inventory override and Shopify availability
  const { override } = useInventoryOverride(node.handle);
  const shopifyAvailable = node.variants?.edges?.some(v => v.node.availableForSale) ?? true;
  const forcedSoldOut = isForceSoldOut(override);
  const isAvailable = getEffectiveAvailability(override, shopifyAvailable);
  const price = parseFloat(node.priceRange.minVariantPrice.amount);
  const firstVariant = node.variants?.edges?.[0]?.node;

  // Check if product is new (has "new" or "new arrivals" tag, or is a gradient product)
  const productTags = node.tags || [];
  const isNewProduct = productTags.some((tag: string) => 
    tag.toLowerCase() === 'new' || 
    tag.toLowerCase() === 'new arrivals' ||
    tag.toLowerCase() === 'new arrival'
  ) || (node.title?.toLowerCase().includes('gradient'));

  // Check if this is the GRIP PRO spray product - link to dedicated landing page
  const isGripProSpray = node.title?.toLowerCase().includes('grip pro') && 
    node.title?.toLowerCase().includes('spray');
  const productLink = isGripProSpray ? '/grip-pro' : `/product/${node.handle}`;

  // Extract color options from product
  const colorOption = node.options?.find(opt => opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour');
  const colors = colorOption?.values || [];

  // Map color names to hex codes (common colors)
  const colorMap: Record<string, string> = {
    'black': '#000000',
    'white': '#FFFFFF',
    'red': '#EF4444',
    'blue': '#3B82F6',
    'green': '#22C55E',
    'yellow': '#EAB308',
    'orange': '#F97316',
    'purple': '#A855F7',
    'pink': '#EC4899',
    'gray': '#6B7280',
    'grey': '#6B7280',
    'navy': '#1E3A5A',
    'lime': '#C8E320',
    'neon': '#C8E320',
    'turquoise': '#14B8A6',
    'teal': '#0D9488',
    'coral': '#F97171',
    'mint': '#A7F3D0',
    'lavender': '#C4B5FD',
    'gold': '#F59E0B',
    'silver': '#9CA3AF',
    'brown': '#92400E',
    'beige': '#D4C4A8',
    'cream': '#FFFDD0',
    'maroon': '#7F1D1D',
    'burgundy': '#800020',
    'charcoal': '#374151',
    'slate': '#475569',
    'rose': '#FB7185',
    'sky': '#38BDF8',
    'indigo': '#6366F1',
    'violet': '#8B5CF6',
    'fuchsia': '#D946EF',
    'emerald': '#10B981',
    'cyan': '#06B6D4',
    'amber': '#F59E0B'
  };
  const getColorHex = (colorName: string): string => {
    const lowerName = colorName.toLowerCase();
    for (const [key, hex] of Object.entries(colorMap)) {
      if (lowerName.includes(key)) {
        return hex;
      }
    }
    // Default gradient for unknown colors
    return `hsl(${Math.abs(colorName.charCodeAt(0) * 7) % 360}, 70%, 50%)`;
  };
  const inWishlist = isInWishlist(node.id);
  const inCompare = isInCompare(node.id);
  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    toggleItem({
      id: node.id,
      name: node.title,
      styleNumber: node.handle,
      price: price,
      color: colors[0] ? getColorHex(colors[0]) : '#C8E320',
      image: imageUrl || ''
    });
    toast.success(inWishlist ? 'Removed from wishlist' : 'Added to wishlist!');
  };
  const handleCompareToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (inCompare) {
      removeFromCompare(node.id);
      toast.success('Removed from compare');
    } else if (compareItems.length >= 4) {
      toast.error('You can compare up to 4 products');
    } else {
      addToCompare({
        id: node.id,
        name: node.title,
        styleNumber: node.handle,
        price: price,
        colors: colors.map(c => getColorHex(c)),
        image: imageUrl || '',
        category: 'grips',
        description: node.description || '',
        collection: '',
        isNew: isNewProduct,
        isBestSeller: showTopRatedBadge,
        rating: 5,
      } as any);
      toast.success('Added to compare');
    }
  };
  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (forcedSoldOut) {
      toast.error("Back in stock soon — sign up to be notified");
      return;
    }
    if (!isAvailable) {
      toast.error("This product is currently out of stock");
      return;
    }
    if (!firstVariant) {
      toast.error("This product is not available");
      return;
    }
    const cartItem: CartItem = {
      product,
      variantId: firstVariant.id,
      variantTitle: firstVariant.title,
      price: firstVariant.price,
      quantity: 1,
      selectedOptions: firstVariant.selectedOptions || []
    };
    addItem(cartItem);
    toast.success("Added to cart!", {
      description: node.title,
      position: "top-center"
    });
  };
  const handleQuickShop = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowQuickShop(true);
  };
  return (
    <Link 
      to={productLink} 
      className="group relative bg-card rounded-lg overflow-hidden block border border-transparent 
                 hover:border-border/50 hover:shadow-lg hover:shadow-foreground/5 transition-all duration-300 h-full flex flex-col"
      onMouseEnter={() => setIsHovered(true)} 
      onMouseLeave={() => {
        setIsHovered(false);
        setShowQuickShop(false);
      }}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-muted/30">
        {imageUrl ? (
          <>
            {/* Primary Image */}
            <img 
              src={`${imageUrl}&width=400`}
              srcSet={`${imageUrl}&width=200 200w, ${imageUrl}&width=300 300w, ${imageUrl}&width=400 400w`}
              sizes="(max-width: 640px) 200px, (max-width: 1024px) 300px, 400px"
              alt={node.images?.edges?.[0]?.node?.altText || node.title} 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              width={400}
              height={400}
            />
            {/* Secondary Image - Slides from right on hover */}
            {secondImageUrl && (
              <img 
                src={`${secondImageUrl}&width=400`}
                srcSet={`${secondImageUrl}&width=200 200w, ${secondImageUrl}&width=300 300w, ${secondImageUrl}&width=400 400w`}
                sizes="(max-width: 640px) 200px, (max-width: 1024px) 300px, 400px"
                alt={node.images?.edges?.[1]?.node?.altText || `${node.title} - alternate view`} 
                className="absolute inset-0 w-full h-full object-cover transition-all duration-500 ease-out
                           translate-x-full opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
                loading="lazy"
                width={400}
                height={400}
              />
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            No image
          </div>
        )}
        
        {/* #1 Top Rated Badge - Top Left */}
        {showTopRatedBadge && (
          <div className="absolute top-1.5 sm:top-2 md:top-3 left-1.5 sm:left-2 md:left-3 z-10">
            <span className="bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-black text-[9px] sm:text-[10px] md:text-xs font-black px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full shadow-lg flex items-center gap-0.5 sm:gap-1 border border-amber-600/30">
              <span className="text-amber-900">#1</span>
              <span>TOP RATED</span>
              <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-700" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </span>
          </div>
        )}
        
        {/* Custom Badge - Top Left (priority over NEW) */}
        {!showTopRatedBadge && customBadge && (
          <div className="absolute top-1.5 sm:top-2 md:top-3 left-1.5 sm:left-2 md:left-3 z-10">
            <span className={`text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full shadow-lg ${customBadge.className || 'bg-accent text-accent-foreground'}`}>
              {customBadge.text}
            </span>
          </div>
        )}
        
        {/* NEW Badge - Top Left (only if not showing top rated or custom badge) */}
        {!showTopRatedBadge && !customBadge && isNewProduct && (
          <div className="absolute top-1.5 sm:top-2 md:top-3 left-1.5 sm:left-2 md:left-3 z-10">
            <span className="bg-primary text-primary-foreground text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full shadow-lg animate-pulse">
              NEW
            </span>
          </div>
        )}
        
        {/* Action Buttons - Top Right - Hidden on mobile, visible on desktop */}
        <TooltipProvider delayDuration={200}>
          <div className={`hidden md:flex absolute top-3 right-3 flex-col gap-2 transition-all duration-300 ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'}`}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={handleWishlistToggle} 
                  aria-label={inWishlist ? `Remove ${node.title} from wishlist` : `Add ${node.title} to wishlist`}
                  className={`flex items-center justify-center w-4 h-4 sm:w-7 sm:h-7 md:w-10 md:h-10 rounded-full shadow-sm !border sm:!border-2 ${inWishlist ? 'bg-primary text-primary-foreground' : 'btn-fill-rise btn-fill-dark'}`}
                >
                  <Heart className={`w-2 h-2 sm:w-3.5 sm:h-3.5 md:w-5 md:h-5 ${inWishlist ? 'fill-current' : ''}`} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">
                {inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={handleCompareToggle} 
                  aria-label={inCompare ? `Remove ${node.title} from compare` : `Add ${node.title} to compare`}
                  className={`flex items-center justify-center w-4 h-4 sm:w-7 sm:h-7 md:w-10 md:h-10 rounded-full shadow-sm !border sm:!border-2 ${inCompare ? 'bg-primary text-primary-foreground' : 'btn-fill-rise btn-fill-dark'}`}
                >
                  <GitCompare className="w-2 h-2 sm:w-3.5 sm:h-3.5 md:w-5 md:h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">
                {inCompare ? 'Remove from compare' : 'Compare'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={handleQuickShop} 
                  aria-label={`Quick view ${node.title}`}
                  className="flex items-center justify-center w-4 h-4 sm:w-7 sm:h-7 md:w-10 md:h-10 rounded-full shadow-sm !border sm:!border-2 btn-fill-rise btn-fill-dark"
                >
                  <Eye className="w-2 h-2 sm:w-3.5 sm:h-3.5 md:w-5 md:h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">
                Quick view
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
        
        {/* Quick Shop Button - Bottom - Hidden on mobile */}
        <div className={`absolute bottom-0 left-0 right-0 p-2 md:p-3 transition-all duration-300 hidden md:block ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <button 
            onClick={handleQuickShop} 
            aria-label={`Quick shop ${node.title}`}
            className="w-full py-2 md:py-2.5 rounded-full font-medium text-xs md:text-sm btn-fill-rise btn-fill-dark"
          >
            Quick Shop
          </button>
        </div>

        {/* Out of Stock Overlay */}
        {!isAvailable && (
          <div className="absolute top-1.5 sm:top-2 md:top-3 left-1.5 sm:left-2 md:left-3 z-20">
            <span className="bg-destructive text-destructive-foreground text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full shadow-lg">
              {forcedSoldOut ? 'BACK SOON' : 'OUT OF STOCK'}
            </span>
          </div>
        )}
        {!isAvailable && (
          <div className="absolute inset-0 bg-background/40 z-10" />
        )}
      </div>
      
      {/* Product Info */}
      <div className="p-2 sm:p-3 md:p-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">
            Grizzlee Gripz
          </span>
          {featureBadges && featureBadges.length > 0 && (
            <TooltipProvider delayDuration={200}>
              <div className="flex items-center gap-1">
                {featureBadges.map((badge) => {
                  const iconMap = { cushioning: Armchair, "extended-play": Timer, ergonomic: Target, moisture: Droplets, temperature: Thermometer, tournament: Trophy, design: Palette, premium: Sparkles, instagram: Camera };
                  const IconComponent = iconMap[badge.icon] || Target;
                  return (
                    <Tooltip key={badge.icon}>
                      <TooltipTrigger asChild>
                        <span className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 text-foreground">
                          <IconComponent className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {badge.label}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </TooltipProvider>
          )}
        </div>
        <h3 className="font-medium text-xs sm:text-sm md:text-base mb-1 sm:mb-1.5 md:mb-2 text-foreground transition-all line-clamp-2 min-h-[1.75rem] sm:min-h-[2rem] md:min-h-[2.5rem] relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-black after:origin-left after:transition-transform after:duration-300 group-hover:after:scale-x-100">
          {node.title}
        </h3>
        
        {/* Color Swatches */}
        {colors.length > 0 && (
          <div className="flex items-center gap-0.5 sm:gap-1 md:gap-1.5 mb-1.5 sm:mb-2 md:mb-3">
            {colors.slice(0, 4).map((color, i) => (
              <div 
                key={i} 
                className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded-full border border-border/50 shadow-sm cursor-pointer 
                           hover:scale-110 transition-transform duration-200" 
                style={{ backgroundColor: getColorHex(color) }} 
                title={color} 
              />
            ))}
            {colors.length > 4 && (
              <span className="text-[9px] sm:text-[10px] md:text-xs text-muted-foreground ml-0.5 sm:ml-1">
                +{colors.length - 4}
              </span>
            )}
          </div>
        )}
        
        <div className="flex items-center justify-between mt-auto gap-2">
          {!isAvailable ? (
            node.title?.toLowerCase().includes('valentine') ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs sm:text-sm font-semibold line-through text-destructive">
                  ${price.toFixed(2)}
                </span>
                <span className="text-[10px] sm:text-xs text-foreground">
                  Subscribe for Next Grip Drop
                </span>
              </div>
            ) : (
              <span className="text-xs sm:text-sm font-semibold text-destructive">
                {forcedSoldOut ? 'Back Soon' : 'Out of Stock'}
              </span>
            )
          ) : (
            <span className="font-semibold text-sm sm:text-base md:text-lg" style={{ color: '#282828' }}>
              ${price.toFixed(2)}
            </span>
          )}
          <button 
            onClick={handleQuickAdd} 
            disabled={!isAvailable}
            aria-label={`Add ${node.title} to cart`}
            className={`flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full flex-shrink-0 ${
              !isAvailable ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'btn-fill-rise btn-fill-dark'
            }`}
          >
            <ShoppingBag className="w-4 h-4 sm:w-4.5 sm:h-4.5 md:w-5 md:h-5" />
          </button>
        </div>
      </div>
      
      {/* Quick Shop Sidebar */}
      {showQuickShop && <ShopifyQuickShop product={product} onClose={() => setShowQuickShop(false)} />}
    </Link>
  );
};

// Quick Shop component for Shopify products
interface ShopifyQuickShopProps {
  product: ShopifyProduct;
  onClose: () => void;
}
const ShopifyQuickShop = ({
  product,
  onClose
}: ShopifyQuickShopProps) => {
  const {
    node
  } = product;
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const addItem = useCartStore(state => state.addItem);
  const {
    isInWishlist,
    toggleItem
  } = useWishlist();
  const { override } = useInventoryOverride(node.handle);
  const variants = node.variants?.edges || [];
  const selectedVariant = variants[selectedVariantIndex]?.node;
  const price = selectedVariant ? parseFloat(selectedVariant.price.amount) : parseFloat(node.priceRange.minVariantPrice.amount);
  const imageUrl = node.images?.edges?.[0]?.node?.url;
  const inWishlist = isInWishlist(node.id);
  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isForceSoldOut(override)) {
      toast.error("Back in stock soon — sign up to be notified");
      return;
    }
    if (!selectedVariant) {
      toast.error("Please select an option");
      return;
    }
    const cartItem: CartItem = {
      product,
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity: quantity,
      selectedOptions: selectedVariant.selectedOptions || []
    };
    await addItem(cartItem);
    toast.success(`${quantity}x ${node.title} added to cart!`);
    onClose();
  };
  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleItem({
      id: node.id,
      name: node.title,
      styleNumber: node.handle,
      price: price,
      color: '#C8E320',
      image: imageUrl || ''
    });
    toast.success(inWishlist ? 'Removed from wishlist' : 'Added to wishlist!');
  };
   return <div className="absolute inset-y-0 right-0 w-56 sm:w-60 md:w-64 bg-background border-l border-border shadow-xl animate-slide-in-right z-20" onClick={e => e.stopPropagation()}>
      <div className="p-3 sm:p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h3 className="font-semibold text-sm sm:text-base">Quick Shop</h3>
          <button onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }} className="p-1 hover:bg-muted rounded-full transition-colors">
            <span className="sr-only">Close</span>
            ✕
          </button>
        </div>

        {/* Product Info */}
        <div className="mb-3 sm:mb-4">
          <p className="font-medium text-sm sm:text-base line-clamp-2">{node.title}</p>
          <p className="font-bold text-lg sm:text-xl mt-1 text-foreground">${price.toFixed(2)}</p>
        </div>

        {/* Variant Selector */}
        {variants.length > 1 && <div className="mb-3 sm:mb-4">
            <p className="text-xs sm:text-sm font-medium mb-1.5 sm:mb-2">Options</p>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {variants.map((variant, i) => <button key={variant.node.id} onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setSelectedVariantIndex(i);
          }} className={`min-w-[36px] sm:min-w-[40px] px-2 py-1.5 rounded text-[11px] sm:text-xs font-medium border transition-all ${selectedVariantIndex === i ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary/50 bg-background'} ${!variant.node.availableForSale ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!variant.node.availableForSale}>
                  {variant.node.title === 'Default Title' ? 'Default' : variant.node.title}
                </button>)}
            </div>
          </div>}

        {/* Quantity */}
        <div className="mb-4 sm:mb-6">
          <p className="text-xs sm:text-sm font-medium mb-1.5 sm:mb-2">Quantity</p>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setQuantity(Math.max(1, quantity - 1));
          }} className="p-1.5 sm:p-2 border border-border rounded-lg hover:bg-muted transition-colors">
              −
            </button>
            <span className="font-medium w-6 sm:w-8 text-center text-sm sm:text-base">{quantity}</span>
            <button onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setQuantity(quantity + 1);
          }} className="p-1.5 sm:p-2 border border-border rounded-lg hover:bg-muted transition-colors">
              +
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-auto space-y-1.5 sm:space-y-2">
          <button className="w-full btn-fill-rise btn-fill-dark py-2.5 sm:py-3 rounded-full font-medium text-xs sm:text-sm flex items-center justify-center gap-1.5 sm:gap-2" onClick={handleAddToCart}>
            <ShoppingBag className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Add to Cart
          </button>
          <button className="w-full py-2.5 sm:py-3 rounded-full font-medium text-xs sm:text-sm border border-border hover:bg-muted transition-colors flex items-center justify-center gap-1.5 sm:gap-2" onClick={handleWishlistToggle}>
            <Heart className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${inWishlist ? 'fill-primary text-primary' : ''}`} />
            {inWishlist ? 'Saved' : 'Wishlist'}
          </button>
        </div>
      </div>
    </div>;
};